/**
 * Org Knowledge Hub management page — b2b-3-2
 *
 * Route: /org/knowledge
 *
 * Features:
 * - Org admins can view all org-owned videos and articles
 * - Create new org-private videos (storage_key, title, metadata, visibility)
 * - Create new org-private knowledge articles (markdown)
 * - Update/delete org-owned content
 * - Visibility badge (public / org_only / private)
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { BookOpen, Video, Plus, Trash2, Edit3, Eye, EyeOff, Globe, Building2 } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Visibility = 'public' | 'org_only' | 'private';

interface OrgVideo {
  id: string;
  title: string;
  description?: string;
  video_type: string;
  difficulty?: string;
  topic_tags: string[];
  duration_sec?: number;
  storage_key: string;
  thumbnail_key?: string;
  is_published: boolean;
  is_public: boolean;
  org_id?: number;
  visibility: Visibility;
  created_at?: string;
}

interface OrgArticle {
  id: string;
  slug: string;
  title: string;
  category: string;
  tags: string[];
  content_md: string;
  is_published: boolean;
  org_id?: number;
  visibility: Visibility;
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchOrgVideos(): Promise<{ items: OrgVideo[]; total: number }> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/videos`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch org videos');
  return res.json();
}

async function fetchOrgArticles(): Promise<{ items: OrgArticle[]; total: number }> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/articles`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to fetch org articles');
  return res.json();
}

async function createOrgVideo(body: Omit<OrgVideo, 'id' | 'created_at' | 'org_id'>): Promise<OrgVideo> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/videos`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Create failed' }));
    throw new Error(err.detail || 'Create failed');
  }
  return res.json();
}

async function updateOrgVideo(id: string, body: Partial<OrgVideo>): Promise<OrgVideo> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/videos/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

async function deleteOrgVideo(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/videos/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
}

async function createOrgArticle(body: {
  slug: string;
  title: string;
  category: string;
  tags: string[];
  content_md: string;
  is_published: boolean;
  visibility: Visibility;
}): Promise<OrgArticle> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/articles`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Create failed' }));
    throw new Error(err.detail || 'Create failed');
  }
  return res.json();
}

async function updateOrgArticle(id: string, body: Partial<OrgArticle>): Promise<OrgArticle> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/articles/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Update failed');
  return res.json();
}

async function deleteOrgArticle(id: string): Promise<void> {
  const res = await fetch(`${API_URL}/api/v1/orgs/knowledge/articles/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error('Delete failed');
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const VISIBILITY_LABELS: Record<Visibility, string> = {
  public: 'Public',
  org_only: 'Org Only',
  private: 'Private',
};

const VISIBILITY_COLORS: Record<Visibility, string> = {
  public: 'bg-green-100 text-green-800',
  org_only: 'bg-blue-100 text-blue-800',
  private: 'bg-gray-100 text-gray-700',
};

function VisibilityIcon({ visibility }: { visibility: Visibility }) {
  if (visibility === 'public') return <Globe className="w-3 h-3 inline mr-1" />;
  if (visibility === 'org_only') return <Building2 className="w-3 h-3 inline mr-1" />;
  return <EyeOff className="w-3 h-3 inline mr-1" />;
}

function VisibilityBadge({ visibility }: { visibility: Visibility }) {
  return (
    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${VISIBILITY_COLORS[visibility]}`}>
      <VisibilityIcon visibility={visibility} />
      {VISIBILITY_LABELS[visibility]}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Add Video form
// ---------------------------------------------------------------------------

function AddVideoForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    title: '',
    description: '',
    video_type: 'educational',
    difficulty: '',
    storage_key: '',
    thumbnail_key: '',
    visibility: 'org_only' as Visibility,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.title.trim() || !form.storage_key.trim()) {
      setError('Title and storage key are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createOrgVideo({
        title: form.title,
        description: form.description || undefined,
        video_type: form.video_type,
        difficulty: form.difficulty || undefined,
        topic_tags: [],
        storage_key: form.storage_key,
        thumbnail_key: form.thumbnail_key || undefined,
        is_published: false,
        is_public: false,
        visibility: form.visibility,
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Org Video</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
            <input
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              rows={2}
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Video Type</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={form.video_type}
                onChange={e => setForm(f => ({ ...f, video_type: e.target.value }))}
              >
                <option value="educational">Educational</option>
                <option value="brand">Brand</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Visibility</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={form.visibility}
                onChange={e => setForm(f => ({ ...f, visibility: e.target.value as Visibility }))}
              >
                <option value="org_only">Org Only</option>
                <option value="public">Public</option>
                <option value="private">Private</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">GCS Storage Key *</label>
            <input
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono"
              placeholder="videos/org/filename.mp4"
              value={form.storage_key}
              onChange={e => setForm(f => ({ ...f, storage_key: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Thumbnail Key</label>
            <input
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono"
              placeholder="thumbnails/org/filename.jpg"
              value={form.thumbnail_key}
              onChange={e => setForm(f => ({ ...f, thumbnail_key: e.target.value }))}
            />
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-blue-600 text-white rounded px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Add Video'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border rounded px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add Article form
// ---------------------------------------------------------------------------

function AddArticleForm({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    slug: '',
    title: '',
    category: 'General',
    content_md: '',
    visibility: 'org_only' as Visibility,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.slug.trim() || !form.title.trim() || !form.content_md.trim()) {
      setError('Slug, title and content are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createOrgArticle({
        slug: form.slug,
        title: form.title,
        category: form.category,
        tags: [],
        content_md: form.content_md,
        is_published: false,
        visibility: form.visibility,
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-lg shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Org Article</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Slug *</label>
              <input
                className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono"
                placeholder="my-org-article"
                value={form.slug}
                onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <input
                className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
            <input
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Content (Markdown) *</label>
            <textarea
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white font-mono"
              rows={6}
              value={form.content_md}
              onChange={e => setForm(f => ({ ...f, content_md: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Visibility</label>
            <select
              className="w-full border rounded px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              value={form.visibility}
              onChange={e => setForm(f => ({ ...f, visibility: e.target.value as Visibility }))}
            >
              <option value="org_only">Org Only</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
          {error && <p className="text-red-600 text-sm">{error}</p>}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 bg-blue-600 text-white rounded px-4 py-2 text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Add Article'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border rounded px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type Tab = 'videos' | 'articles';

export default function OrgKnowledgeHub() {
  const [tab, setTab] = useState<Tab>('videos');
  const [showAddVideo, setShowAddVideo] = useState(false);
  const [showAddArticle, setShowAddArticle] = useState(false);
  const qc = useQueryClient();

  const videosQuery = useQuery({
    queryKey: ['org-knowledge-videos'],
    queryFn: fetchOrgVideos,
  });

  const articlesQuery = useQuery({
    queryKey: ['org-knowledge-articles'],
    queryFn: fetchOrgArticles,
  });

  const toggleVideoPublished = useMutation({
    mutationFn: ({ id, is_published }: { id: string; is_published: boolean }) =>
      updateOrgVideo(id, { is_published }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-knowledge-videos'] }),
  });

  const removeVideo = useMutation({
    mutationFn: (id: string) => deleteOrgVideo(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-knowledge-videos'] }),
  });

  const toggleArticlePublished = useMutation({
    mutationFn: ({ id, is_published }: { id: string; is_published: boolean }) =>
      updateOrgArticle(id, { is_published }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-knowledge-articles'] }),
  });

  const removeArticle = useMutation({
    mutationFn: (id: string) => deleteOrgArticle(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org-knowledge-articles'] }),
  });

  const videos = videosQuery.data?.items ?? [];
  const articles = articlesQuery.data?.items ?? [];

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-blue-600" />
            Org Knowledge Hub
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage private videos and articles for your organisation members.
          </p>
        </div>
        <div className="flex gap-2">
          {tab === 'videos' && (
            <button
              onClick={() => setShowAddVideo(true)}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Add Video
            </button>
          )}
          {tab === 'articles' && (
            <button
              onClick={() => setShowAddArticle(true)}
              className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> Add Article
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setTab('videos')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'videos'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          <Video className="w-4 h-4" /> Videos ({videos.length})
        </button>
        <button
          onClick={() => setTab('articles')}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'articles'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'
          }`}
        >
          <BookOpen className="w-4 h-4" /> Articles ({articles.length})
        </button>
      </div>

      {/* Videos tab */}
      {tab === 'videos' && (
        <div>
          {videosQuery.isLoading && (
            <div className="text-center py-8 text-gray-500">Loading videos…</div>
          )}
          {videosQuery.isError && (
            <div className="text-center py-8 text-red-600">
              {videosQuery.error instanceof Error ? videosQuery.error.message : 'Failed to load videos.'}
              <br />
              <span className="text-sm text-gray-500">You must be an org admin to access this page.</span>
            </div>
          )}
          {!videosQuery.isLoading && !videosQuery.isError && videos.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Video className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No org videos yet. Add a video to get started.</p>
            </div>
          )}
          <div className="space-y-3">
            {videos.map(video => (
              <div
                key={video.id}
                className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{video.title}</span>
                    <VisibilityBadge visibility={video.visibility} />
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      video.is_published
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {video.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono truncate">
                    {video.storage_key}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => toggleVideoPublished.mutate({ id: video.id, is_published: !video.is_published })}
                    title={video.is_published ? 'Unpublish' : 'Publish'}
                    className="p-1.5 text-gray-500 hover:text-blue-600 rounded"
                  >
                    {video.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${video.title}"?`)) {
                        removeVideo.mutate(video.id);
                      }
                    }}
                    className="p-1.5 text-gray-500 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Articles tab */}
      {tab === 'articles' && (
        <div>
          {articlesQuery.isLoading && (
            <div className="text-center py-8 text-gray-500">Loading articles…</div>
          )}
          {articlesQuery.isError && (
            <div className="text-center py-8 text-red-600">
              {articlesQuery.error instanceof Error ? articlesQuery.error.message : 'Failed to load articles.'}
              <br />
              <span className="text-sm text-gray-500">You must be an org admin to access this page.</span>
            </div>
          )}
          {!articlesQuery.isLoading && !articlesQuery.isError && articles.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p>No org articles yet. Add an article to get started.</p>
            </div>
          )}
          <div className="space-y-3">
            {articles.map(article => (
              <div
                key={article.id}
                className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 dark:text-white truncate">{article.title}</span>
                    <VisibilityBadge visibility={article.visibility} />
                    <span className="text-xs text-gray-500 dark:text-gray-400">/{article.slug}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      article.is_published
                        ? 'bg-green-100 text-green-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {article.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{article.category}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => toggleArticlePublished.mutate({ id: article.id, is_published: !article.is_published })}
                    title={article.is_published ? 'Unpublish' : 'Publish'}
                    className="p-1.5 text-gray-500 hover:text-blue-600 rounded"
                  >
                    {article.is_published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${article.title}"?`)) {
                        removeArticle.mutate(article.id);
                      }
                    }}
                    className="p-1.5 text-gray-500 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddVideo && (
        <AddVideoForm
          onClose={() => setShowAddVideo(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['org-knowledge-videos'] })}
        />
      )}
      {showAddArticle && (
        <AddArticleForm
          onClose={() => setShowAddArticle(false)}
          onSaved={() => qc.invalidateQueries({ queryKey: ['org-knowledge-articles'] })}
        />
      )}
    </div>
  );
}
